import { useEventHandler, useUpdateRef } from '@zambezi/sdk/common'
import React, {
  Children,
  cloneElement,
  createRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'

/**
 * @param {import('./interfaces').UseMenuProps} props
 * @param {import('react').RefObject} ref
 */
export function useMenu (
  {
    children,
    className,
    onItemSelect,
    onItemKeyDown,
    orientation,
    prefix,
    suffix,
    withAutoOpen,
    isPositioned,
    openItem: propsOpenItem,
    isOpen,
    elementRef
  },
  ref
) {
  const isVertical = orientation !== 'horizontal'
  /** @type {import('react').RefObject<import('react').RefObject<HTMLElement>[]>} */
  const navItemsRef = useRef([])
  const itemRef = useRef()
  const openTimeRef = useRef(0)
  const [openItem, _setOpenItem] = useState(() => {
    if (propsOpenItem === undefined) {
      if (withAutoOpen) return getOpenIndex(children)
      return -1
    }
    return propsOpenItem
  })

  /**
   * @type {(index: number) => void}
   */
  const setOpenItem = useCallback(index => {
    const isOpen = index !== -1
    const now = Date.now()
    const hasNotJustOpened = now - openTimeRef.current > 100
    if (isOpen || hasNotJustOpened) {
      _setOpenItem(index)
      openTimeRef.current = now
    }
  })

  /**
   * @type {(index: number) => void}
   */
  const focus = useCallback((index = 0) => {
    const items = navItemsRef.current
    if (index === -1) {
      focusItem(items[items.length - 1])
    } else {
      focusItem(items[index])
    }
  }, [])
  const refObject = useMemo(
    () => ({
      focus,
      open: setOpenItem,
      close: setOpenItem.bind(null, -1),
      navItemsRef
    }),
    []
  )

  useUpdateRef(ref, elementRef || itemRef, refObject)
  useEffect(componentDidUpdate, [Children.count(children)])
  useLayoutEffect(positionMenu, [isPositioned, isOpen])
  useEventHandler('resize', positionMenu, document.defaultView)
  useEventHandler('scroll', positionMenu, document.defaultView)

  const content = (
    <ul
      role={isVertical ? 'menu' : 'menubar'}
      className={className}
      ref={elementRef ? null : itemRef}
    >
      {prefix}
      {Children.toArray(children).map(createMenuItem)}
      {suffix}
    </ul>
  )

  return { content, navItemsRef, openItem, setOpenItem, focus }

  function componentDidUpdate () {
    navItemsRef.current.length = Children.count(children)
  }

  function positionMenu () {
    if (isPositioned && isOpen) {
      const menu = (elementRef || itemRef).current
      const menuParent = menu.parentNode
      const viewportWidth = document.documentElement.clientWidth
      const menuBounds = menu.getBoundingClientRect()
      const parentBounds = menuParent.getBoundingClientRect()
      const left = parentBounds.right

      menu.style.top = parentBounds.top + 'px'
      menu.style.left =
        Math.min(Math.max(left, 0), viewportWidth - menuBounds.width) + 'px'
    }
  }

  /**
   * @param {import('react').ReactElement} child
   * @param {number} index
   */
  function createMenuItem (child, index) {
    navItemsRef.current[index] = navItemsRef.current[index] || createRef()
    const ref = navItemsRef.current[index]

    if (
      child.type &&
      child.type.componentName &&
      child.type.componentName.slice(0, 6) === 'zb.Nav'
    ) {
      return cloneElement(child, {
        isMenuOpen: openItem === index,
        ref,
        onSelect: handleChildCallback(event => {
          onItemSelect(event)
          focus(index)
        }, child.props.onSelect),
        onClose: setOpenItem.bind(null, -1),
        onOpen: setOpenItem.bind(null, index),
        onKeyDown: handleKeyDown.bind(null, index),
        orientation,
        withAutoOpen
      })
    }

    return cloneElement(child, {
      onKeyDown: handleKeyDown.bind(null, index),
      ref
    })
  }

  /**
   * @param {number} index
   * @param {import('react').KeyboardEvent} event
   */
  function handleKeyDown (index, event) {
    const { length } = navItemsRef.current
    const focusedIndex = getFocusedIndex(event, event.key, index, isVertical)
    if (event.key === 'Enter') return false
    if (event.key === 'Tab' && focusedIndex === length) {
      focus(-1)
    } else if (event.shiftKey && event.key === 'Tab' && focusedIndex === -1) {
      focus(length)
    } else if (focusedIndex != null) {
      focus(focusedIndex === -1 ? length - 1 : focusedIndex % length)
      event.preventDefault()
    }
    onItemKeyDown(event, focusedIndex == null ? index : focusedIndex, -1)
  }
}

/**
 * @template {(any) => void} T
 * @param {T} defaultCallback
 * @param {T} childCallback
 * @returns {T}
 */
export function handleChildCallback (defaultCallback, childCallback) {
  return childCallback ? handleCallback : defaultCallback
  function handleCallback () {
    childCallback.apply(this, arguments)
    defaultCallback.apply(this, arguments)
  }
}

/**
 * @param {string} key
 * @param {number} index
 * @param {boolean} isVertical
 * @returns {number}
 */
function getFocusedIndex (event, key, index, isVertical) {
  if (event.shiftKey && key === 'Tab') return index - 1
  switch (key) {
    case 'Home':
      return 0
    case 'End':
      return -1
    case 'Tab':
    case 'ArrowRight': // falls through
    case 'ArrowDown':
      if (
        isVertical
          ? key === 'ArrowDown' || key === 'Tab'
          : key === 'ArrowRight' || key === 'Tab'
      ) {
        return index + 1
      }
      return index
    case 'ArrowLeft': // falls through
    case 'ArrowUp':
      if (isVertical ? key === 'ArrowUp' : key === 'ArrowLeft') {
        return index - 1
      }
      return index
    default:
      return index
  }
}

/**
 * @param {import('react').ReactChild} child
 * @returns {boolean}
 */
function hasSelectedChild (child) {
  const { props } = child || {}
  if (child && props) {
    if (props.isSelected) return true
    return Children.toArray(props.children).map(hasSelectedChild).some(Boolean)
  }
  return false
}

/**
 * @param {import('react').ReactChildren} children
 * @returns {number}
 */
function getOpenIndex (children) {
  let openIndex = -1
  Children.forEach(children, (child, index) => {
    if (openIndex === -1 && hasSelectedChild(child)) {
      openIndex = index
    }
  })
  return openIndex
}

/**
 * @param {import('react').RefObject<HTMLElement>} item
 * @param {number} index
 */
export function focusItem (item, index) {
  item && item.current && item.current.focus(index)
}

export function noop () {}
